import type { MaybeRefOrGetter } from 'vue';

interface CmsContext {
  baseURL: string;
  csrftoken: Ref<string | undefined | null>;
}

function useCmsContext(): CmsContext {
  const config = useRuntimeConfig();
  const requestUrl = useRequestURL();
  const csrftoken = useCookie<string>('csrftoken');

  const baseURL = import.meta.server
    ? (config.cmsBaseUrl as string)
    : requestUrl.origin;

  return { baseURL, csrftoken };
}

function buildUrl(baseURL: string, endpoint: string, params?: Record<string, unknown>): string {
  const url = new URL(endpoint, baseURL);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) {
        if (Array.isArray(v)) {
          v.forEach(item => url.searchParams.append(k, String(item)));
        }
        else {
          url.searchParams.set(k, String(v));
        }
      }
    }
  }
  return url.toString();
}

export function useCms() {
  const ctx = useCmsContext();

  async function cmsFetch<T>(endpoint: MaybeRefOrGetter<string>, params?: Record<string, unknown>): Promise<T> {
    const url = buildUrl(ctx.baseURL, toValue(endpoint), params);
    try {
      const data = await $fetch<T>(url, {
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'X-CSRFToken': ctx.csrftoken.value ?? '',
        },
      });
      return data;
    }
    catch (error) {
      if (import.meta.server) {
        const status = (error as any)?.response?.status ?? (error as any)?.statusCode;
        const body = (error as any)?.data ?? (error as any)?.response?._data;
        console.error(`[CMS] ERR ${status ?? '???'} ${url}`, (error as any)?.message ?? error, body ? JSON.stringify(body, null, 2) : '');
      }
      throw error;
    }
  }

  return { cmsFetch };
}
